import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react-components";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  setColumnMapping,
  setCurrentStep,
  updateStatementMapping,
} from "../../../redux/Commission/commissionStatementSlice";

const NAVY = "#205393";
const T1 = "#0f172a";
const T2 = "#475569";
const T3 = "#94a3b8";
const GRN = "#2b8a3e";
const GRNL = "#e6f9f0";
const RED = "#c92a2a";
const ACC = "#1e4a8a";
const ACCL = "#e8f0fb";
const BRD = "#e2e5ea";
const BRD2 = "#c8cdd5";
const SUR2 = "#f0f0f0";

const WA_FIELDS = [
  { key: "caseRef", label: "Case Reference", required: true, hint: "Non-empty string" },
  { key: "actual", label: "Actual (Amount Received)", required: true, hint: "Numeric only" },
  { key: "clientName", label: "Client Name", required: false, hint: "Optional — display only" },
] as const;

const NOT_MAPPED = "__not_mapped__";

const StepColumnMapping: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { parsedHeaders, parsedRows, columnMapping, statementId, loading, loadingMapping, errorMapping } = useSelector(
    (s: RootState) => s.commissionStatement
  );

  const [localMapping, setLocalMapping] = useState(() => {
    const has = (v: string) => !!v && parsedHeaders.includes(v);
    return {
      caseRef: has(columnMapping.caseRef) ? columnMapping.caseRef : "",
      actual: has(columnMapping.actual) ? columnMapping.actual : "",
      clientName: has(columnMapping.clientName || "") ? (columnMapping.clientName || "") : "",
    };
  });
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleChange = (field: string, value: string) => {
    const newMapping = { ...localMapping, [field]: value === NOT_MAPPED ? "" : value };
    setLocalMapping(newMapping);
    dispatch(setColumnMapping(newMapping));
  };

  const validate = (): boolean => {
    if (!localMapping.caseRef || !parsedHeaders.includes(localMapping.caseRef)) {
      setValidationError("Case Reference column is required.");
      return false;
    }
    if (!localMapping.actual || !parsedHeaders.includes(localMapping.actual)) {
      setValidationError("Actual (Amount) column is required.");
      return false;
    }
    const badRow = parsedRows.find((row) => {
      const raw = String(row[localMapping.actual] ?? "").trim();
      const val = raw.replace(/[£$€¥,\s]/g, "");
      return val !== "" && isNaN(Number(val));
    });
    if (badRow) {
      setValidationError(
        `Column "${localMapping.actual}" contains a non-numeric value: "${badRow[localMapping.actual]}". All Actual values must be numeric.`
      );
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleNext = async () => {
    if (!validate() || !statementId) return;
    const result = await dispatch(
      updateStatementMapping({ statementId, columnMapping: localMapping })
    );
    if (updateStatementMapping.fulfilled.match(result)) {
      dispatch(setCurrentStep(3));
    }
  };

  const getMappingStatus = (field: string) => {
    const val = localMapping[field as keyof typeof localMapping];
    const required = WA_FIELDS.find((f) => f.key === field)?.required;
    if (!val || !parsedHeaders.includes(val)) return required ? "required" : "optional";
    return "mapped";
  };

  const badgeStyle = (status: string): React.CSSProperties => {
    if (status === "mapped") return { background: GRNL, color: GRN };
    if (status === "required") return { background: "#ffe6e6", color: RED };
    return { background: SUR2, color: T3 };
  };

  const badgeLabel = (status: string) => {
    if (status === "mapped") return "Mapped";
    if (status === "required") return "Required — not mapped";
    return "Optional";
  };

  return (
    <div>
      <div style={{ background: "#fff", border: `1px solid ${BRD}`, borderRadius: "5px", padding: "14px 16px" }}>
        <div style={{ fontSize: "13px", fontWeight: 600, color: T1, marginBottom: "4px" }}>
          Step 3 — Column Mapping
        </div>
        <p style={{ fontSize: "12px", color: T3, marginBottom: "12px" }}>
          Select which column from your uploaded file maps to each wealth.assyst field.
          Case Reference and Actual are required.
        </p>

        {/* Mapping grid */}
        <div style={{ border: `1px solid ${BRD}`, borderRadius: "5px", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 32px 1fr 110px", background: SUR2, borderBottom: `2px solid ${BRD}` }}>
            <span style={{ padding: "7px 11px", fontSize: "11px", fontWeight: 600, color: T2, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              WA Column
            </span>
            <span style={{ padding: "7px 11px", fontSize: "11px", fontWeight: 600, color: T2, textAlign: "center", borderLeft: `1px solid ${BRD}`, borderRight: `1px solid ${BRD}` }}>
              →
            </span>
            <span style={{ padding: "7px 11px", fontSize: "11px", fontWeight: 600, color: T2, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              File Column
            </span>
            <span style={{ padding: "7px 11px", fontSize: "11px", fontWeight: 600, color: T2, letterSpacing: "0.04em", textTransform: "uppercase", background: SUR2, borderLeft: `1px solid ${BRD}` }}>
              Status
            </span>
          </div>

          {/* Rows */}
          {WA_FIELDS.map((field, idx) => {
            const status = getMappingStatus(field.key);
            return (
              <div
                key={field.key}
                style={{ display: "grid", gridTemplateColumns: "1fr 32px 1fr 110px", borderBottom: idx < WA_FIELDS.length - 1 ? `1px solid ${BRD}` : "none" }}
              >
                {/* WA field name */}
                <div style={{ padding: "7px 11px", fontSize: "12.5px", color: T1, background: "#fff", display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontFamily: "monospace", fontSize: "12px", color: ACC, fontWeight: 500 }}>{field.label}</span>
                  {field.required && <span style={{ color: RED }}>*</span>}
                </div>
                {/* Arrow */}
                <div style={{ padding: "7px 11px", background: SUR2, display: "flex", alignItems: "center", justifyContent: "center", borderLeft: `1px solid ${BRD}`, borderRight: `1px solid ${BRD}`, color: T3, fontSize: "14px" }}>
                  →
                </div>
                {/* Select */}
                <div style={{ padding: "5px 8px", background: "#fff", display: "flex", alignItems: "center" }}>
                  <select
                    value={localMapping[field.key] || NOT_MAPPED}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    style={{
                      height: "26px",
                      border: `1px solid ${BRD2}`,
                      borderRadius: "5px",
                      padding: "0 20px 0 7px",
                      fontSize: "12px",
                      color: T1,
                      background: "#fff",
                      width: "100%",
                      appearance: "none",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    <option value={NOT_MAPPED}>— Not mapped —</option>
                    {(() => {
                      const currentVal = localMapping[field.key as keyof typeof localMapping];
                      const usedByOthers = new Set(
                        Object.entries(localMapping)
                          .filter(([k, v]) => k !== field.key && v !== "")
                          .map(([, v]) => v)
                      );
                      return parsedHeaders
                        .filter(h => !usedByOthers.has(h) || h === currentVal)
                        .map(h => <option key={h} value={h}>{h}</option>);
                    })()}
                  </select>
                </div>
                {/* Status badge */}
                <div style={{ padding: "7px 11px", background: SUR2, borderLeft: `1px solid ${BRD}`, display: "flex", alignItems: "center" }}>
                  <span style={{
                    display: "inline-flex",
                    padding: "2px 7px",
                    borderRadius: "20px",
                    fontSize: "11px",
                    fontWeight: 600,
                    ...badgeStyle(status),
                  }}>
                    {badgeLabel(status)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {validationError && (
          <div style={{ fontSize: "12.5px", color: RED, marginTop: "8px" }}>{validationError}</div>
        )}
        {errorMapping && (
          <div style={{ background: "#ffe6e6", border: `1px solid ${RED}`, borderRadius: "5px", padding: "8px 12px", fontSize: "12.5px", color: RED, marginTop: "8px" }}>
            {errorMapping}
          </div>
        )}

        {/* Button row */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "14px", paddingTop: "12px", borderTop: `1px solid ${BRD}` }}>
          <button
            onClick={() => dispatch(setCurrentStep(1))}
            style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: "5px", fontSize: "12.5px", fontWeight: 500, padding: "0 14px", height: "32px", cursor: "pointer" }}
          >
            ← Back
          </button>
          <div style={{ flex: 1 }} />
          <button
            onClick={handleNext}
            disabled={loadingMapping || !localMapping.caseRef || !localMapping.actual}
            style={{
              background: (!loadingMapping && localMapping.caseRef && localMapping.actual) ? NAVY : "#c8cdd5",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              fontSize: "12.5px",
              fontWeight: 500,
              padding: "0 16px",
              height: "32px",
              cursor: (!loadingMapping && localMapping.caseRef && localMapping.actual) ? "pointer" : "not-allowed",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {loadingMapping ? <Spinner size="tiny" /> : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepColumnMapping;
